import React, { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { Button, H1, Icon, IconButton, Input, InternationalContact, colors, useGraphQLMutation, useGraphQLQuery } from '@tviklax/ui-components';
import { DashboardContainer } from '../../features/dashboard/components/styles';
import { useRole } from '../../context/RoleContext';

// lucide-react has no WhatsApp glyph (it's a generic icon set, not brand
// icons) — inlined here the same way InternationalContact inlines its own
// verified-badge SVG, rather than pulling in a brand-icon dependency for one glyph.
const WhatsAppIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
  </svg>
);

const PAGE_SIZE = 20;

// WhatsApp is always offered (it's internet-based, so it's free for the
// sender regardless of which country either side is in). An sms: shortcut is
// layered on top only for +1 numbers, as a bonus for domestic US-to-US
// texting — never a replacement, since a sender on an Indian carrier often
// can't send international SMS to a +1 number cheaply or at all, and would
// otherwise be left with no working option.
const isUsPhoneNumber = (phone: string) => phone.replace(/[\s()-]/g, '').startsWith('+1');

const MY_ORG_CONTACTS_QUERY = gql`
  query MyOrgContacts($search: String, $limit: Int, $offset: Int) {
    myOrgContacts(search: $search, limit: $limit, offset: $offset) {
      totalCount
      items {
        id
        name
        role
        team
        email
        phone
        visibility
        isPrimary
        createdAt
        createdByName
        updatedAt
        updatedByName
      }
    }
  }
`;

const MY_TRADE_PARTNER_CONTACTS_QUERY = gql`
  query MyTradePartnerContacts($search: String, $limit: Int, $offset: Int) {
    myTradePartnerContacts(search: $search, limit: $limit, offset: $offset) {
      totalCount
      items {
        id
        name
        role
        team
        email
        phone
        partnerId
        partnerName
      }
    }
  }
`;

const ADD_ORG_CONTACT_MUTATION = gql`
  mutation AddOrgContact($input: AddContactInput!) {
    addOrgContact(input: $input) {
      contact {
        id
        name
        role
        team
        email
        phone
        visibility
        isPrimary
      }
      possibleDuplicates {
        id
        name
        role
        team
        email
        phone
      }
      restorableContact {
        id
        name
        role
        team
        email
        phone
        visibility
        deletedAt
      }
    }
  }
`;

const RESTORE_ORG_CONTACT_MUTATION = gql`
  mutation RestoreOrgContact($id: Int!, $input: AddContactInput!) {
    restoreOrgContact(id: $id, input: $input) {
      contact {
        id
        name
        role
        team
        email
        phone
        visibility
        isPrimary
      }
    }
  }
`;

const UPDATE_ORG_CONTACT_MUTATION = gql`
  mutation UpdateOrgContact($input: UpdateContactInput!) {
    updateOrgContact(input: $input) {
      contact {
        id
        name
        role
        team
        email
        phone
        visibility
        isPrimary
      }
      possibleDuplicates {
        id
        name
        role
        team
        email
        phone
      }
    }
  }
`;

const DELETE_ORG_CONTACT_MUTATION = gql`
  mutation DeleteOrgContact($id: Int!) {
    deleteOrgContact(id: $id) {
      success
      message
    }
  }
`;

interface OrgContact {
  id: number;
  name: string;
  role?: string | null;
  team?: string | null;
  email?: string | null;
  phone?: string | null;
  visibility?: string | null;
  isPrimary: boolean;
  createdAt?: string | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  partnerName?: string | null;
}

interface PossibleDuplicate {
  id: number;
  name: string;
  role?: string | null;
  team?: string | null;
  email?: string | null;
  phone?: string | null;
}

interface RestorableContact {
  id: number;
  name: string;
  role?: string | null;
  team?: string | null;
  email?: string | null;
  phone?: string | null;
  visibility?: string | null;
  deletedAt?: string | null;
}

type ContactsTab = 'team' | 'partners';

const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const addedMeta = (contact: OrgContact) => {
  if (!contact.createdAt) return null;
  const date = new Date(contact.createdAt);
  if (Number.isNaN(date.getTime())) return null;
  const formattedDate = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return contact.createdByName ? `Added by ${contact.createdByName} · ${formattedDate}` : `Added ${formattedDate}`;
};

const editedMeta = (contact: OrgContact) => {
  // updated_at also gets bumped by incidental DB touches unrelated to a real
  // edit (e.g. older linkage migrations) — updated_by_name is only ever set
  // by the update-contact mutation, so require it as positive proof of an
  // actual edit rather than inferring one from a timestamp delta.
  if (!contact.updatedAt || !contact.updatedByName) return null;
  const date = new Date(contact.updatedAt);
  if (Number.isNaN(date.getTime())) return null;
  const formattedDate = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `Edited by ${contact.updatedByName} · ${formattedDate}`;
};

const formatDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export const ContactsPage: React.FC = () => {
  const { role } = useRole();
  const partnerLabel = role === 'supplier' ? 'Customers' : 'Suppliers';
  const showPartnerTab = role === 'customer' || role === 'supplier';

  const [activeTab, setActiveTab] = useState<ContactsTab>('team');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [team, setTeam] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneValid, setPhoneValid] = useState(true);
  const [visibleToPartner, setVisibleToPartner] = useState(false);
  const [formError, setFormError] = useState('');
  const [listError, setListError] = useState('');
  const [possibleDuplicates, setPossibleDuplicates] = useState<PossibleDuplicate[]>([]);
  const [restorableContact, setRestorableContact] = useState<RestorableContact | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const switchTab = (tab: ContactsTab) => {
    setActiveTab(tab);
    setSearchInput('');
    setDebouncedSearch('');
    setPage(0);
    setShowForm(false);
    resetForm();
  };

  const queryVariables = { search: debouncedSearch || null, limit: PAGE_SIZE, offset: page * PAGE_SIZE };

  const { data, loading, refetch } = useGraphQLQuery<{
    myOrgContacts: { items: OrgContact[]; totalCount: number };
  }>(MY_ORG_CONTACTS_QUERY, {
    showErrorToast: false,
    variables: queryVariables,
    skip: activeTab !== 'team',
  });

  const { data: partnerData, loading: partnerLoading } = useGraphQLQuery<{
    myTradePartnerContacts: { items: OrgContact[]; totalCount: number };
  }>(MY_TRADE_PARTNER_CONTACTS_QUERY, {
    showErrorToast: false,
    variables: queryVariables,
    skip: activeTab !== 'partners',
  });

  // Apollo drops `data` and flips `loading` back to true on every search/page
  // variable change (it's a genuinely new query, not a cache hit), which
  // would otherwise blank the whole list and flash "Loading…" on each
  // keystroke. Keep showing the last real result — dimmed — until the next
  // one actually arrives, instead of tearing it down in between.
  const [displayedTeam, setDisplayedTeam] = useState<{ items: OrgContact[]; totalCount: number } | null>(null);
  useEffect(() => {
    if (data?.myOrgContacts) {
      setDisplayedTeam(data.myOrgContacts);
    }
  }, [data]);

  const [displayedPartners, setDisplayedPartners] = useState<{ items: OrgContact[]; totalCount: number } | null>(null);
  useEffect(() => {
    if (partnerData?.myTradePartnerContacts) {
      setDisplayedPartners(partnerData.myTradePartnerContacts);
    }
  }, [partnerData]);

  const displayed = activeTab === 'team' ? displayedTeam : displayedPartners;
  const isLoading = activeTab === 'team' ? loading : partnerLoading;
  const isInitialLoad = isLoading && !displayed;

  const [addContact, { loading: saving }] = useGraphQLMutation(ADD_ORG_CONTACT_MUTATION, {
    showSuccessToast: false,
    showErrorToast: false,
  });

  const [updateContact, { loading: updating }] = useGraphQLMutation(UPDATE_ORG_CONTACT_MUTATION, {
    showSuccessToast: false,
    showErrorToast: false,
  });

  const [deleteContact, { loading: deleting }] = useGraphQLMutation(DELETE_ORG_CONTACT_MUTATION, {
    showSuccessToast: false,
    showErrorToast: false,
  });

  const [restoreContact, { loading: restoring }] = useGraphQLMutation(RESTORE_ORG_CONTACT_MUTATION, {
    showSuccessToast: false,
    showErrorToast: false,
  });

  const contacts = displayed?.items ?? [];
  const totalCount = displayed?.totalCount ?? 0;
  const pageStart = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const pageEnd = Math.min(totalCount, page * PAGE_SIZE + contacts.length);
  const hasNextPage = page * PAGE_SIZE + contacts.length < totalCount;
  const isEditing = editingId !== null;
  const isSaving = saving || updating;

  function resetForm() {
    setEditingId(null);
    setName('');
    setContactRole('');
    setTeam('');
    setEmail('');
    setPhone('');
    setPhoneValid(true);
    setVisibleToPartner(false);
    setFormError('');
    setPossibleDuplicates([]);
    setRestorableContact(null);
  }

  const startEdit = (contact: OrgContact) => {
    setEditingId(contact.id);
    setName(contact.name);
    setContactRole(contact.role ?? '');
    setTeam(contact.team ?? '');
    setEmail(contact.email ?? '');
    setPhone(contact.phone ?? '');
    setPhoneValid(true);
    setVisibleToPartner(contact.visibility === 'Customer-visible');
    setFormError('');
    setListError('');
    setPossibleDuplicates([]);
    setRestorableContact(null);
    setConfirmDeleteId(null);
    setShowForm(true);
  };

  const submitContact = async (force: boolean) => {
    const contactFields = {
      name: name.trim(),
      role: contactRole.trim() || null,
      team: team.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      visibleToTradePartner: visibleToPartner,
      force,
    };
    const result = editingId
      ? await updateContact({ variables: { input: { id: editingId, ...contactFields } } })
      : await addContact({ variables: { input: contactFields } });
    const payload = editingId ? result.data?.updateOrgContact : result.data?.addOrgContact;
    if (payload?.contact) {
      resetForm();
      setShowForm(false);
      refetch();
      return;
    }
    setPossibleDuplicates(payload?.possibleDuplicates ?? []);
    const restorable: RestorableContact | null = payload?.restorableContact ?? null;
    setRestorableContact(restorable);
    if (restorable) {
      // Pre-fill from the removed contact's own stored details, not
      // whatever's currently in the form — otherwise clicking "Restore"
      // silently wipes any field the caller hadn't happened to type
      // (e.g. typing only a name blanks out the old role/team/email/phone).
      setName(restorable.name);
      setContactRole(restorable.role ?? '');
      setTeam(restorable.team ?? '');
      setEmail(restorable.email ?? '');
      setPhone(restorable.phone ?? '');
      setPhoneValid(true);
      setVisibleToPartner(restorable.visibility === 'Customer-visible');
    }
  };

  const handleRestore = async () => {
    if (!restorableContact) return;
    setFormError('');
    try {
      const result = await restoreContact({
        variables: {
          id: restorableContact.id,
          input: {
            name: name.trim(),
            role: contactRole.trim() || null,
            team: team.trim() || null,
            email: email.trim() || null,
            phone: phone.trim() || null,
            visibleToTradePartner: visibleToPartner,
            force: true,
          },
        },
      });
      if (result.data?.restoreOrgContact?.contact) {
        resetForm();
        setShowForm(false);
        refetch();
      } else {
        setFormError("Couldn't restore that contact — please try again.");
      }
    } catch {
      setFormError("Couldn't restore that contact — please try again.");
    }
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!name.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (phone.trim() && !phoneValid) {
      setFormError('That phone number doesn’t look right — check the country code and digits.');
      return;
    }
    try {
      await submitContact(false);
    } catch {
      setFormError(isEditing ? "Couldn't save those changes — please try again." : "Couldn't add that contact — please try again.");
    }
  };

  const handleSubmitAnyway = async () => {
    setFormError('');
    try {
      await submitContact(true);
    } catch {
      setFormError(isEditing ? "Couldn't save those changes — please try again." : "Couldn't add that contact — please try again.");
    }
  };

  const handleDelete = async (id: number) => {
    setListError('');
    try {
      const result = await deleteContact({ variables: { id } });
      const payload = result.data?.deleteOrgContact;
      if (payload?.success) {
        setConfirmDeleteId(null);
        if (contacts.length === 1 && page > 0) {
          setPage(page - 1);
        } else {
          refetch();
        }
      } else {
        setListError(payload?.message || "Couldn't remove that contact — please try again.");
        setConfirmDeleteId(null);
      }
    } catch {
      setListError("Couldn't remove that contact — please try again.");
      setConfirmDeleteId(null);
    }
  };

  return (
    <DashboardContainer>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <H1 style={{ margin: 0 }}>Your contacts</H1>
            <div style={{ fontSize: 14, color: colors.gray500 }}>
              {activeTab === 'team'
                ? 'The people at your organization — no separate CRM needed, this is who your trade partners and Tvikla OS see.'
                : `A read-only directory of your ${partnerLabel.toLowerCase()}' contacts who've marked themselves visible to trade partners — scoped to the ones you've actually done business with.`}
            </div>
          </div>
          {activeTab === 'team' && (
            <Button
              onClick={() => {
                resetForm();
                setShowForm((v) => !v);
              }}
            >
              {showForm ? 'Cancel' : '+ Add contact'}
            </Button>
          )}
        </div>

        {showPartnerTab && (
          <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${colors.gray200}` }}>
            <button
              onClick={() => switchTab('team')}
              style={{
                padding: '10px 16px',
                fontSize: 14,
                fontWeight: 600,
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'team' ? `2px solid ${colors.primary}` : '2px solid transparent',
                color: activeTab === 'team' ? colors.primary : colors.gray500,
                cursor: 'pointer',
              }}
            >
              Your team
            </button>
            <button
              onClick={() => switchTab('partners')}
              style={{
                padding: '10px 16px',
                fontSize: 14,
                fontWeight: 600,
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'partners' ? `2px solid ${colors.primary}` : '2px solid transparent',
                color: activeTab === 'partners' ? colors.primary : colors.gray500,
                cursor: 'pointer',
              }}
            >
              {partnerLabel}
            </button>
          </div>
        )}

        {activeTab === 'team' && showForm && (
          <div style={{ background: '#fff', border: `1px solid ${colors.gray200}`, borderRadius: 14, padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1c1f26' }}>
              {isEditing ? 'Edit contact' : 'New contact'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Input label="Name" placeholder="e.g. Priya Sharma" value={name} onChange={(e) => setName(e.target.value)} />
              <Input label="Role" placeholder="e.g. Finance, Logistics" value={contactRole} onChange={(e) => setContactRole(e.target.value)} />
              <Input label="Team" placeholder="e.g. Operations" value={team} onChange={(e) => setTeam(e.target.value)} />
              <Input label="Email" type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
              <InternationalContact
                variant="work"
                label="Phone"
                value={phone}
                onChange={setPhone}
                onValidate={setPhoneValid}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: colors.gray600, cursor: 'pointer' }}>
              <input type="checkbox" checked={visibleToPartner} onChange={(e) => setVisibleToPartner(e.target.checked)} />
              Visible to your trade partners (suppliers/buyers on shared orders)
            </label>
            {formError && <div style={{ fontSize: 13, color: colors.error }}>{formError}</div>}

            {possibleDuplicates.length > 0 && (
              <div style={{ background: '#fff9e8', border: `1px solid ${colors.warning}`, borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#7a5c00' }}>
                  This looks like it might already be in your contacts:
                </div>
                {possibleDuplicates.map((dup) => (
                  <div key={dup.id} style={{ fontSize: 13, color: colors.gray600 }}>
                    <strong>{dup.name}</strong>
                    {[dup.role, dup.team].filter(Boolean).length > 0 && ` — ${[dup.role, dup.team].filter(Boolean).join(' · ')}`}
                    {dup.email && ` · ${dup.email}`}
                    {dup.phone && ` · ${dup.phone}`}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button onClick={handleSubmitAnyway} disabled={isSaving}>
                    {isSaving ? 'Saving…' : isEditing ? 'Save anyway' : 'Add anyway'}
                  </Button>
                  <Button variant="outline" onClick={() => setPossibleDuplicates([])}>Cancel</Button>
                </div>
              </div>
            )}

            {restorableContact && (
              <div style={{ background: '#fff9e8', border: `1px solid ${colors.warning}`, borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#7a5c00' }}>
                  This matches a contact you removed{formatDate(restorableContact.deletedAt) ? ` on ${formatDate(restorableContact.deletedAt)}` : ''}:
                </div>
                <div style={{ fontSize: 13, color: colors.gray600 }}>
                  <strong>{restorableContact.name}</strong>
                  {[restorableContact.role, restorableContact.team].filter(Boolean).length > 0 &&
                    ` — ${[restorableContact.role, restorableContact.team].filter(Boolean).join(' · ')}`}
                  {restorableContact.email && ` · ${restorableContact.email}`}
                  {restorableContact.phone && ` · ${restorableContact.phone}`}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button onClick={handleRestore} disabled={restoring}>
                    {restoring ? 'Restoring…' : 'Restore this contact'}
                  </Button>
                  <Button variant="outline" onClick={handleSubmitAnyway} disabled={isSaving || restoring}>
                    Add as new anyway
                  </Button>
                  <Button variant="outline" onClick={() => setRestorableContact(null)}>Cancel</Button>
                </div>
              </div>
            )}

            {possibleDuplicates.length === 0 && !restorableContact && (
              <div>
                <Button onClick={handleSubmit} disabled={isSaving}>
                  {isSaving ? (isEditing ? 'Saving…' : 'Adding…') : isEditing ? 'Save changes' : 'Add contact'}
                </Button>
              </div>
            )}
          </div>
        )}

        {listError && <div style={{ fontSize: 13, color: colors.error }}>{listError}</div>}

        <Input
          placeholder={
            activeTab === 'team'
              ? 'Search by name, email, role, or team…'
              : `Search by name, email, role, team, or ${partnerLabel === 'Customers' ? 'customer' : 'supplier'}…`
          }
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <div
          style={{
            background: '#fff',
            border: `1px solid ${colors.gray200}`,
            borderRadius: 14,
            overflow: 'hidden',
            opacity: isLoading && !isInitialLoad ? 0.6 : 1,
            transition: 'opacity 150ms',
          }}
        >
          {isInitialLoad && (
            <div style={{ padding: 22, color: colors.gray500 }}>Loading contacts…</div>
          )}
          {!isInitialLoad && !isLoading && contacts.length === 0 && debouncedSearch && (
            <div style={{ padding: 22, color: colors.gray500 }}>
              No contacts match “{debouncedSearch}”.
            </div>
          )}
          {!isInitialLoad && !isLoading && contacts.length === 0 && !debouncedSearch && activeTab === 'team' && (
            <div style={{ padding: 22, color: colors.gray500 }}>
              No contacts yet — add the people at your organization so they show up here instead of a separate system.
            </div>
          )}
          {!isInitialLoad && !isLoading && contacts.length === 0 && !debouncedSearch && activeTab === 'partners' && (
            <div style={{ padding: 22, color: colors.gray500 }}>
              No {partnerLabel.toLowerCase()} contacts to show yet — they'll appear here once a {partnerLabel === 'Customers' ? 'customer' : 'supplier'}{' '}
              you've shared an order with marks a contact visible to trade partners.
            </div>
          )}
          {!isInitialLoad && contacts.map((contact, index) => (
            <div
              key={contact.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '18px 22px',
                borderBottom: index === contacts.length - 1 ? 'none' : `1px solid ${colors.gray100}`,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: colors.gray100,
                  color: colors.gray600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {initials(contact.name) || <Icon name="User" size={18} />}
              </div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#1c1f26' }}>{contact.name}</span>
                  {activeTab === 'team' && contact.isPrimary && (
                    <span style={{ background: '#fdefe7', color: colors.primary, fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Primary
                    </span>
                  )}
                  {activeTab === 'team' && (
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 20,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        background: contact.visibility === 'Customer-visible' ? '#e8f6ec' : colors.gray100,
                        color: contact.visibility === 'Customer-visible' ? colors.success : colors.gray500,
                      }}
                    >
                      {contact.visibility === 'Customer-visible' ? 'Visible to partners' : 'Internal only'}
                    </span>
                  )}
                  {activeTab === 'partners' && contact.partnerName && (
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 20,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        background: colors.gray100,
                        color: colors.gray600,
                      }}
                    >
                      {contact.partnerName}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: colors.gray500 }}>
                  {[contact.role, contact.team].filter(Boolean).join(' · ') || '—'}
                </div>
                {activeTab === 'team' && addedMeta(contact) && (
                  <div style={{ fontSize: 11.5, color: colors.gray400 }}>{addedMeta(contact)}</div>
                )}
                {activeTab === 'team' && editedMeta(contact) && (
                  <div style={{ fontSize: 11.5, color: colors.gray400 }}>{editedMeta(contact)}</div>
                )}
              </div>
              <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13, color: colors.gray500 }}>
                {contact.email && <div>{contact.email}</div>}
                {contact.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                    <span>{contact.phone}</span>
                    <a
                      href={`tel:${contact.phone}`}
                      title={`Call ${contact.name}`}
                      style={{ display: 'flex', color: colors.gray500 }}
                    >
                      <Icon name="Phone" size={14} />
                    </a>
                    {/* WhatsApp is internet-based and free to send either
                        direction, so it stays as the constant fallback for
                        every contact. SMS is layered on top only for +1
                        numbers, as a bonus shortcut for domestic US-to-US
                        texting — never a replacement, since international
                        SMS is often unavailable or costly for a sender on
                        an Indian carrier reaching a +1 number. */}
                    {isUsPhoneNumber(contact.phone) && (
                      <a
                        href={`sms:${contact.phone}`}
                        title={`Text ${contact.name}`}
                        style={{ display: 'flex', color: '#0A84FF' }}
                      >
                        <Icon name="MessageCircle" size={14} />
                      </a>
                    )}
                    <a
                      href={`https://wa.me/${contact.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Message ${contact.name} on WhatsApp`}
                      style={{ display: 'flex', color: '#25D366' }}
                    >
                      <WhatsAppIcon size={14} />
                    </a>
                  </div>
                )}
              </div>

              {activeTab === 'team' && !contact.isPrimary && (
                confirmDeleteId === contact.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12, flexShrink: 0 }}>
                    <span style={{ fontSize: 12, color: colors.gray500 }}>Remove?</span>
                    <Button variant="outline" onClick={() => handleDelete(contact.id)} disabled={deleting}>
                      {deleting ? 'Removing…' : 'Yes'}
                    </Button>
                    <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 4, marginLeft: 12, flexShrink: 0 }}>
                    <IconButton variant="ghost" size="sm" aria-label={`Edit ${contact.name}`} onClick={() => startEdit(contact)}>
                      <Icon name="Pencil" size={14} />
                    </IconButton>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${contact.name}`}
                      onClick={() => {
                        setListError('');
                        setConfirmDeleteId(contact.id);
                      }}
                    >
                      <Icon name="Trash2" size={14} />
                    </IconButton>
                  </div>
                )
              )}
            </div>
          ))}
        </div>

        {totalCount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: colors.gray500 }}>
            <div>
              Showing {pageStart}–{pageEnd} of {totalCount} contact{totalCount === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outline" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || isLoading}>
                Previous
              </Button>
              <Button variant="outline" onClick={() => setPage((p) => p + 1)} disabled={!hasNextPage || isLoading}>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardContainer>
  );
};

export default ContactsPage;
